import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { customCollectionsAPI, CustomCollection } from "../services/api";

const CustomCollections: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [collections, setCollections] = useState<CustomCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollection, setNewCollection] = useState<{
    name: string;
    description: string;
    exerciseType: "clozes" | "verbs" | "both";
  }>({
    name: "",
    description: "",
    exerciseType: "both",
  });
  const [creating, setCreating] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] =
    useState<CustomCollection | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCollections();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await customCollectionsAPI.getCollections(
        getAccessTokenSilently
      );
      setCollections(response.data.collections);
    } catch (err: any) {
      setError("Failed to load custom collections.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      await customCollectionsAPI.createCollection(
        {
          name: newCollection.name,
          description: newCollection.description,
          isPublic: false,
          exerciseType: newCollection.exerciseType,
        },
        getAccessTokenSilently
      );
      setNewCollection({
        name: "",
        description: "",
        exerciseType: "both",
      });
      setShowCreateForm(false);
      fetchCollections();
    } catch (err: any) {
      setError("Failed to create collection.");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const openDeleteModal = (collection: CustomCollection) => {
    setCollectionToDelete(collection);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCollectionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!collectionToDelete) return;

    setDeleting(true);
    try {
      await customCollectionsAPI.deleteCollection(
        collectionToDelete.id,
        getAccessTokenSilently
      );
      closeDeleteModal();
      fetchCollections();
    } catch (err: any) {
      setError("Failed to delete collection.");
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleStartPractice = async (customCollectionId: number) => {
    try {
      const response = await customCollectionsAPI.getPracticeInfo(
        customCollectionId,
        getAccessTokenSilently
      );
      const collectionId = response.data.collection_id;
      navigate(`/practice/${collectionId}`);
    } catch (err: any) {
      setError("Failed to start practice. Please try again.");
      console.error(err);
    }
  };

  const handleStartVerbPractice = async (customCollectionId: number) => {
    try {
      const response = await customCollectionsAPI.getPracticeInfo(
        customCollectionId,
        getAccessTokenSilently
      );
      const collectionId = response.data.collection_id;
      navigate(`/verbs/${collectionId}`);
    } catch (err: any) {
      setError("Failed to start verb practice. Please try again.");
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-1 sm:mb-2">
            Decks
          </h1>
          <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400">
            Tailor exercise sets to your specific learning goals.
          </p>
        </div>

        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all font-medium whitespace-nowrap self-start sm:self-auto"
        >
          {showCreateForm ? "Cancel" : "New Deck"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Create Collection Form */}
      {showCreateForm && (
        <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-4 sm:p-6 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-4 sm:mb-6">
            Create New Deck
          </h2>

          <form onSubmit={handleCreateCollection}>
            <div className="mb-4">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
              >
                Deck Name *
              </label>
              <input
                type="text"
                id="name"
                value={newCollection.name}
                onChange={(e) =>
                  setNewCollection({ ...newCollection, name: e.target.value })
                }
                className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
                required
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2"
              >
                Description
              </label>
              <textarea
                id="description"
                value={newCollection.description}
                onChange={(e) =>
                  setNewCollection({
                    ...newCollection,
                    description: e.target.value,
                  })
                }
                rows={3}
                className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200 transition-colors text-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creating ? "Creating..." : "Create Deck"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Collections List */}
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700">
        {collections.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <div className="text-zinc-500 dark:text-zinc-400 mb-4">
              <svg
                className="mx-auto h-10 w-10 sm:h-12 sm:w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
            </div>
            <h3 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              You don't have any decks yet
            </h3>
            <p className="text-sm sm:text-base text-zinc-500 dark:text-zinc-400">
              Create a deck to get started.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {collections.map((collection) => (
              <div
                key={collection.id}
                className="p-4 sm:p-6 hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-base sm:text-lg font-medium text-zinc-900 dark:text-zinc-100">
                        {collection.name}
                      </h3>
                    </div>
                    {collection.description && (
                      <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400 mb-2">
                        {collection.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 gap-x-2">
                      <span>{collection.verse_count || 0} verses</span>
                      <span>•</span>
                      <span>
                        Created{" "}
                        {new Date(collection.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                    <Link
                      to={`/decks/${collection.id}/edit`}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-all font-medium text-center"
                    >
                      {collection.verse_count && collection.verse_count > 0
                        ? "Edit Verses"
                        : "Add Verses"}
                    </Link>
                    {collection.verse_count && collection.verse_count > 0 && (
                      <>
                        {(collection.exercise_type === "clozes" ||
                          collection.exercise_type === "both") && (
                          <button
                            onClick={() => handleStartPractice(collection.id)}
                            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-all font-medium"
                          >
                            Practice Clozes
                          </button>
                        )}
                        {(collection.exercise_type === "verbs" ||
                          collection.exercise_type === "both") && (
                          <button
                            onClick={() =>
                              handleStartVerbPractice(collection.id)
                            }
                            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg transition-all font-medium"
                          >
                            Practice Verbs
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => openDeleteModal(collection)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && collectionToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeDeleteModal}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-zinc-800 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700 w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Warning Icon */}
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 text-center mb-2">
              Delete Deck
            </h3>

            {/* Message */}
            <p className="text-zinc-600 dark:text-zinc-400 text-center mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                "{collectionToDelete.name}"
              </span>
              ? This action cannot be undone.
            </p>

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 text-zinc-700 dark:text-zinc-300 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Deleting...
                  </span>
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomCollections;
